import { AuthProvider, OAuthActionType, OAuthSuccessResponse, XeroConnection } from '@avanzu/types';
import { apiClient } from '.';

export interface XeroConnectionWithStatus extends XeroConnection {
  isConnected: boolean;
}

/**
 * Load Xero connection status for an organisation
 */
export const loadXeroConnectionStatus = async (
  organisationId: string
): Promise<XeroConnectionWithStatus | null> => {
  try {
    const response = await apiClient.get(`/organisations/${organisationId}/xero/status`);

    if (response.ok) {
      const result = await response.json();
      const xeroConnection = result.connection as XeroConnection;
      const isConnected = Boolean(xeroConnection && xeroConnection.xeroTenantId);

      return {
        isConnected,
        ...xeroConnection,
      };
    }
    return null;
  } catch (error) {
    console.error('Failed to load Xero connection status:', error);
    return null;
  }
};

/**
 * Disconnect Xero integration for an organisation
 */
export const disconnectXero = async (organisationId: string): Promise<boolean> => {
  try {
    const response = await apiClient.post(
      `/auth/oauth/xero/organisation/${organisationId}/disconnect`
    );
    return response.ok;
  } catch (error) {
    console.error('Error disconnecting Xero integration:', error);
    return false;
  }
};

/**
 * Open Xero OAuth popup for connection
 */
export const openXeroConnectPopup = (
  returnUrl: string = '/settings/integrations'
): Window | null => {
  const width = 500;
  const height = 800;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    `/auth/bridge?popup=true&action=${OAuthActionType.CONNECT}&returnUrl=${returnUrl}`,
    'xero-oauth',
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
  );

  return popup;
};

/**
 * Check if a message event is a valid Xero OAuth response
 */
export const isXeroOAuthResponse = (event: MessageEvent): boolean => {
  if (event.origin !== window.location.origin) return false;

  const response = event.data as OAuthSuccessResponse;
  return response.action === OAuthActionType.CONNECT && response.provider === AuthProvider.XERO;
};

/**
 * Extract success status from OAuth response
 */
export const getOAuthResponseSuccess = (event: MessageEvent): boolean => {
  const response = event.data as OAuthSuccessResponse;
  return response.success;
};
